import React from 'react'
import ConfirmList from 'components/elements/confirm/ConfirmList'
import DestinationIcon from 'components/elements/icons/DestinationIcon'
import RequesterIcon from 'components/elements/icons/RequesterIcon'
import ContractInformationContent from 'components/elements/items/confirmContents/ContractInformationContent'
import DestinationConfirmContent, {
  DestinationConfirmContentProps,
} from 'components/elements/items/confirmContents/DestinationConfirmContent'
import MapConfirmContent from 'components/elements/items/confirmContents/MapConfirmContent'
import SurroundingConfirmContent from 'components/elements/items/confirmContents/SurroundingConfirmContent'
import TroubleConfirmContent from 'components/elements/items/confirmContents/TroubleConfirmContent'
import { PAGE_PATH } from 'constants/pagePath'
import { VEHICLE_STATE } from 'constants/qnState'
import {
  DestinationTypes,
  initialQnEditState,
  QnStateProps,
} from 'states/QnState'
import { isDestinationTarget } from 'utils/qnState'
import { fail, StatusProps } from 'utils/status'
import AnimationModal from 'components/elements/modal/AnimationModal'
import styled from 'styled-components'
import TrackingMap from 'components/elements/maps/TrackingMap'
import { CONFIRM_MAP_OPTIONS } from 'constants/map'
import PrimaryButton from 'components/elements/buttons/PrimaryButton'
import SecondaryButton from 'components/elements/buttons/SecondaryButton'
import { HistoryStackContext } from 'contexts/HistoryStackContext'
import { useHistory } from 'react-router-dom'
import { transitionWithAction } from 'utils/commonUtils'
import { useEditContractInfo } from 'utils/commonHooks'
import dayjs from 'dayjs'

const ModalMapArea = styled.div`
  width: 300px;
`

const ButtonInnerArea = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  > * {
    margin: 0 4px;
  }
`

/**
 * 要請者の入力情報表示エリア
 */
// 特約の利用の有無が表示対象外となったためコメントアウト(今後再使用する可能性あり)
const ConfirmContentArea: React.FC<ConfirmContentAreaProps> = (props) => {
  const { state, setState, isRequest } = props
  const [editState, setEditingItem] = useEditContractInfo()
  const [isOpenMapModal, setIsOpenMapModal] = React.useState(false)

  const history = useHistory()
  const historyStackContext = React.useContext(HistoryStackContext)

  const getChangeLinkAction = React.useCallback(
    (confirmationChangeType: string) => {
      return (): StatusProps => {
        return setState
          ? setState({
              ...state,
              confirmationChangeType,
            })
          : fail()
      }
    },
    [state]
  )

  const deleteReservation = React.useCallback(() => {
    /** 予約日時の削除 */
    setState
      ? setState({ ...state, reservation: initialQnEditState.reservation })
      : history.push(PAGE_PATH.ERROR)
    setEditingItem({
      ...editState,
      reservation: initialQnEditState.reservation,
    })
  }, [state])

  const handleMapclick = React.useCallback(() => {
    // マップの拡大モーダルを表示
    setIsOpenMapModal(true)
  }, [])

  const handleMapModalClose = React.useCallback(() => {
    // マップの拡大モーダルを非表示
    setIsOpenMapModal(false)
  }, [])

  const handleMapDetermination = React.useCallback(() => {
    // お車の場所選択画面に遷移する
    transitionWithAction(
      historyStackContext,
      'お車の場所(地図モーダル)',
      () => {
        history.push(PAGE_PATH.TROUBLE_PLACE)
      },
      getChangeLinkAction(PAGE_PATH.TROUBLE_PLACE)
    )
  }, [history, historyStackContext, getChangeLinkAction])

  // 予約の時間幅算出
  const endTimeHour = 2

  const endTime = React.useMemo(() => {
    return dayjs(`2000/1/1 ${state.reservation.time}`)
      .add(endTimeHour, 'h')
      .format('HH:mm')
  }, [state.reservation.time])

  const confirmItems = React.useMemo(() => {
    const items = [
      {
        titleLabel: 'お車の場所',
        content: (
          <MapConfirmContent
            component={<RequesterIcon />}
            {...state.location}
            onMapClick={handleMapclick}
          />
        ),
        changeLinkTo: PAGE_PATH.TROUBLE_PLACE,
        changeLinkAction: getChangeLinkAction(PAGE_PATH.TROUBLE_PLACE),
      },
      ...(state.reservation.time && state.reservation.date
        ? [
            {
              titleLabel: '予約日時',
              content: (
                <div>
                  {state.reservation.date} <br />
                  {state.reservation.time}~{endTime}
                </div>
              ),
              changeLinkTo: PAGE_PATH.RESERVATION,
              changeLinkAction: getChangeLinkAction(PAGE_PATH.RESERVATION),
              onDeleteItem: deleteReservation,
              deleteBtnLabel: '今すぐ手配(予約日時の削除)',
            },
          ]
        : []),
      {
        titleLabel: '周辺情報',
        content: (
          <SurroundingConfirmContent locationState={state.locationStateInfo} />
        ),
        changeLinkTo: PAGE_PATH.TROUBLE_PLACE_SURROUNDING,
        changeLinkAction: getChangeLinkAction(
          PAGE_PATH.TROUBLE_PLACE_SURROUNDING
        ),
      },
      {
        titleLabel: 'お車の状況',
        content: (
          <TroubleConfirmContent
            troubleDatetime={state.troubleDatetime.timing}
            troubleDateDetailTime={state.troubleDatetime}
            vehicleState={state.vehicleState}
            vehicleStateInfo={state.vehicleStateInfo}
          />
        ),
        changeLinkTo: PAGE_PATH.VEHICLE_STATE,
        changeLinkAction: getChangeLinkAction(PAGE_PATH.VEHICLE_STATE),
      },
    ]

    if (isDestinationTarget(state)) {
      // 搬送先の設定対象の場合
      const { vehicleStateInfo } = state

      // 搬送先の場所指定かどうかを取得
      const isLocationSelection =
        vehicleStateInfo.destinationType === DestinationTypes.specify

      /** 確認画面に表示する自走できない場合の運び先候補のprops */
      const destinationConfirmContentProps: DestinationConfirmContentProps = {
        // 搬送先指定あり
        [DestinationTypes.specify]: {
          // 地図を表示する
          mapProps: {
            locationName: state.destinationLocation.locationName,
            lat: state.destinationLocation.lat,
            lng: state.destinationLocation.lng,
            address: state.destinationLocation.address,
            component: <DestinationIcon />,
          },
        },
        // 搬送先指定なし、都道府県超え希望
        [DestinationTypes.consider]: {
          // 地図なしで表示
          crossPrefectures: vehicleStateInfo.crossPrefectures,
        },
        // 搬送先指定なし、工場紹介希望
        [DestinationTypes.introduction]:
          // 地図なしで表示
          {},
        // ローカルストレージ空用
        [DestinationTypes['']]: {},
      }[state.vehicleStateInfo.destinationType]

      // 変更リンクから遷移する場所
      const destinationChangeLinkTo = ((): string => {
        if (isLocationSelection) {
          // 搬送先選択画面
          return PAGE_PATH.TRANSPORT_DESTINATION
        }
        // トラブル種類ごとの画面
        switch (state.vehicleState) {
          case VEHICLE_STATE.ACCIDENT:
            return PAGE_PATH.ACCIDENT
          case VEHICLE_STATE.EMPTY_FUEL:
            return PAGE_PATH.EMPTY_FUEL
          case VEHICLE_STATE.ENGINE_TROUBLE:
            return PAGE_PATH.ENGINE_TROUBLE
          case VEHICLE_STATE.FLAT_TIRE:
            return PAGE_PATH.FLAT_TIRE
          case VEHICLE_STATE.FALLING_WHEEL:
            return PAGE_PATH.FALLING_WHEEL
          case VEHICLE_STATE.LOSS_KEY:
            return PAGE_PATH.LOSS_KEY
          case VEHICLE_STATE.OTHERS:
            return PAGE_PATH.OTHERS
          default:
            return PAGE_PATH.ERROR
        }
      })()

      items.push({
        titleLabel: '自走できない場合の運び先候補',
        content: (
          <DestinationConfirmContent {...destinationConfirmContentProps} />
        ),
        changeLinkTo: destinationChangeLinkTo,
        changeLinkAction: getChangeLinkAction(destinationChangeLinkTo),
      })
    }
    items.push({
      titleLabel: 'お客様情報',
      content: <ContractInformationContent contractInfo={state.contractInfo} />,
      changeLinkTo: PAGE_PATH.CONTRACT_INFORMATION,
      changeLinkAction: getChangeLinkAction(PAGE_PATH.CONTRACT_INFORMATION),
    })

    // 特約の利用の有無
    // if (hasSpecialContract(state)) {
    //   const specialContractItems = ((): SpecialContractConfirmContentProps => {
    //     const specialContractItems: Array<SpecialContractItem> = []

    //     // 宿泊・移動費用特約
    //     if (hasRaLodgingTransfer(state)) {
    //       // 移動費用特約
    //       specialContractItems.push({
    //         label: RA_TRANSFER_SC_WISHES.CONFIRMATION.TITLE,
    //         values: [
    //           isSelectedCheck(state.raTransferScWishes, RA_TRANSFER_SC_WISHES.YES)
    //             ? RA_TRANSFER_SC_WISHES.CONFIRMATION.YES
    //             : isSelectedCheck(
    //                 state.raTransferScWishes,
    //                 RA_TRANSFER_SC_WISHES.NO
    //               )
    //             ? RA_TRANSFER_SC_WISHES.CONFIRMATION.NO
    //             : RA_TRANSFER_SC_WISHES.CONFIRMATION.THINKING,
    //         ],
    //       })

    //       // 宿泊費用特約
    //       specialContractItems.push({
    //         label: RA_LODGING_SC_WISHES.CONFIRMATION.TITLE,
    //         values: [
    //           isSelectedCheck(state.raLodgingScWishes, RA_LODGING_SC_WISHES.YES)
    //             ? RA_LODGING_SC_WISHES.CONFIRMATION.YES
    //             : isSelectedCheck(
    //                 state.raLodgingScWishes,
    //                 RA_LODGING_SC_WISHES.NO
    //               )
    //             ? RA_LODGING_SC_WISHES.CONFIRMATION.NO
    //             : RA_LODGING_SC_WISHES.CONFIRMATION.THINKING,
    //         ],
    //       })
    //     }
    //     // 代車費用特約
    //     if (hasRaLcarCost(state)) {
    //       specialContractItems.push({
    //         label: RA_LCAR_COST_SC_WISHES.CONFIRMATION.TITLE,
    //         values: [
    //           isSelectedCheck(
    //             state.raLcarCostScWishes,
    //             RA_LCAR_COST_SC_WISHES.YES
    //           )
    //             ? RA_LCAR_COST_SC_WISHES.CONFIRMATION.YES
    //             : isSelectedCheck(
    //                 state.raLcarCostScWishes,
    //                 RA_LCAR_COST_SC_WISHES.NO
    //               )
    //             ? RA_LCAR_COST_SC_WISHES.CONFIRMATION.NO
    //             : RA_LCAR_COST_SC_WISHES.CONFIRMATION.ALREADY_ARRANGED,
    //         ],
    //       })

    //       if (state.raLcarCostScSupplWishes.text.inputText) {
    //         specialContractItems.push({
    //           label: VEHICLE_STATE_CONFIRM_TITLE.RA_LCAR_COST_SC_SUPPL_WISHES,
    //           values: [state.raLcarCostScSupplWishes.text.inputText],
    //         })
    //       }
    //     }

    //     return {
    //       specialContractItems,
    //     }
    //   })()

    //   items.push({
    //     titleLabel: '特約の利用の有無',
    //     content: specialContractItems && (
    //       <SpecialContractConfirmContent {...specialContractItems} />
    //     ),
    //     changeLinkTo: PAGE_PATH.SPECIAL_CONTRACT,
    //     changeLinkAction: getChangeLinkAction(PAGE_PATH.SPECIAL_CONTRACT),
    //   })
    // }

    return items
  }, [
    state.contractInfo,
    state.destinationLocation,
    state.locationStateInfo,
    state.troubleDatetime,
    state.vehicleState,
    state.vehicleStateInfo,
  ])

  return (
    <>
      <ConfirmList isConfirm isRequest={props.isRequest} items={confirmItems} />
      <AnimationModal
        on={isOpenMapModal}
        type="center"
        animationType="transparent"
        greyBack
        scrollLock
      >
        <ModalMapArea>
          <TrackingMap
            defaultCenter={{
              lat: state.location.lat,
              lng: state.location.lng,
            }}
            markers={[
              {
                component: <RequesterIcon />,
                lat: state.location.lat,
                lng: state.location.lng,
              },
            ]}
            fixed
            height="300px"
            options={CONFIRM_MAP_OPTIONS}
          />
        </ModalMapArea>
        {!isRequest && (
          <PrimaryButton onClick={handleMapDetermination}>
            <ButtonInnerArea>{'変更する'}</ButtonInnerArea>
          </PrimaryButton>
        )}
        <SecondaryButton onClick={handleMapModalClose}>
          {'もどる'}
        </SecondaryButton>
      </AnimationModal>
    </>
  )
}

export type ConfirmContentAreaProps = {
  state: QnStateProps
  setState?: (sampleState: QnStateProps) => StatusProps
  isRequest?: boolean
}

export default ConfirmContentArea
